/**
 * entitySpecCompilers — deterministic EntityDefinition → WindowSpec compilers
 * (Entity OS Phase-1/Faz-6, umbrella #421 / epic #447).
 *
 * PURE + DETERMINISTIC: no LLM, no network, no clock, no randomness, and NO
 * react / react-query. The same EntityDefinition always compiles to the exact
 * same WindowSpec — which is why it is snapshot-testable. This is the "compile,
 * don't interpret" form/list/board path; the cosmetic-overlay / UI-edit tier is a
 * separate binding pass that CONSUMES these outputs (see applyViewOverlay in the
 * web container layer) — it never mutates the compilers.
 *
 * Home (gh#1199, PR-1): these compilers were promoted from the web package to
 * @elmes/shared so a single source drives BOTH the form container (EntityForm) AND
 * the list/board containers (EntityList / EntityBoard), plus the `column-<key>`
 * overlay node-id helper (columnOverlayNodeId) that binds a per-user cosmetic
 * overlay to a column. The web package keeps a thin re-export so existing imports
 * (`./schemaToWindowSpec.js`) resolve unchanged.
 *
 * Output is a serializable WindowSpec conforming to the @elmes/shared windowSpec
 * zod contract (forward-compat: only documented props/ids are emitted). It is the
 * STRUCTURAL template: it carries field ids/labels/placeholders but NO live
 * handlers — `value` / `onChange` / `checked` / `onClick` are injected by the
 * stateful container before rendering (WindowSpec props are JSON, not closures).
 *
 * Field → primitive mapping (Faz-6 PR-4 — routes to the PR-3 primitives now that
 * Textarea / Checkbox / DatePicker exist):
 *   - validation.enum present → Select   (options from the enum)      [overrides type]
 *   - boolean                 → Checkbox (structural `checked: false`; bool binding)
 *   - date | datetime         → DatePicker (structural `value: ''`; YYYY-MM-DD string)
 *   - text                    → Textarea (rows 4, maxLength from validation.maxLength)
 *   - number                  → Input    (type 'number')
 *   - string | json           → Input    (type 'text')
 *
 * A field id is `field-<name>`; the submit button id is `submit-button`. The
 * container binds handlers by these ids and picks the value prop by the emitted
 * node type (Checkbox → `checked`, everything else → `value`).
 */
/** Stable id prefix for a field's input/select node. */
export const FIELD_ID_PREFIX = 'field-';
/** Stable id of the form's submit button node. */
export const SUBMIT_BUTTON_ID = 'submit-button';
/** Build the deterministic node id for a field. */
export function fieldNodeId(fieldName) {
    return `${FIELD_ID_PREFIX}${fieldName}`;
}
/** Stable pseudo-nodeId prefix for a list/board column's cosmetic overlay entry. */
export const COLUMN_ID_PREFIX = 'column-';
/**
 * Deterministic pseudo-nodeId for a list/board COLUMN's cosmetic overlay entry.
 *
 * SINGLE SOURCE OF TRUTH: both the render-time merge helper (applyViewOverlay) and
 * any overlay-editing UI must build a column's overlay key through this helper so
 * the identity can never drift. `key` is the list column key (the field name,
 * snake_case) or the board column value (the status value).
 *
 * Note: list/board columns are NOT WindowSpec nodes (the list/board specs carry a
 * `props.columns` array, not per-column child nodes), so `column-<key>` is a
 * PSEUDO node id — a stable overlay address, never a real renderer node.
 */
export function columnOverlayNodeId(key) {
    return `${COLUMN_ID_PREFIX}${key}`;
}
/**
 * The stable IDENTITY of a list/board column (gh#1261): the list column `key` (field
 * name) or the board column `id` (status value). SINGLE SOURCE OF TRUTH — both the
 * spec→overlay diff (`specDiff.diffColumns`) and the overlay→effective-spec merge
 * (`applyOverlayToSpec.mergeColumnsInProps`) resolve a column's overlay address through
 * this helper so the identity can never drift between the write (diff) and read (merge)
 * sides. Returns `undefined` for a column with no usable string identity — the caller
 * then treats it as non-addressable (a structural change on the diff side; a no-op on
 * the merge side).
 */
export function columnIdentity(col) {
    if (!col || typeof col !== 'object' || Array.isArray(col))
        return undefined;
    const c = col;
    if (typeof c.key === 'string' && c.key.length > 0)
        return c.key;
    if (typeof c.id === 'string' && c.id.length > 0)
        return c.id;
    return undefined;
}
/**
 * WindowSpec node types whose compiled `props.columns` array is a list/board COLUMN set
 * (as opposed to, e.g., a numeric grid `columns` on some future layout primitive). The
 * per-column cosmetic-overlay machinery — the `specDiff` columns-decompose branch and the
 * `applyOverlayToSpec` effective-base column merge — is gated on this set so a `columns`
 * prop on any OTHER node type is never mis-treated as a per-column cosmetic edit
 * (defensive narrowing, gh#1265 NOTE-2).
 */
export const COLUMN_BEARING_NODE_TYPES = ['Table', 'Board'];
const COLUMN_BEARING_NODE_TYPE_SET = new Set(COLUMN_BEARING_NODE_TYPES);
/** True when `type` is a list/board node whose `props.columns` is a column set. */
export function isColumnBearingNodeType(type) {
    return COLUMN_BEARING_NODE_TYPE_SET.has(type);
}
/** Human-readable label from a snake_case field name (deterministic). */
function humanizeFieldName(name) {
    return name
        .split('_')
        .filter((part) => part.length > 0)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}
/** Input `type` for a field type (Select handles enum/boolean separately). */
function inputTypeForField(type) {
    return type === 'number' ? 'number' : 'text';
}
/**
 * Compile a single field into its WindowSpec node.
 *
 * Deterministic: identical FieldDef → identical node. The required flag is
 * reflected into the label (a trailing " *") so the structural template carries
 * the constraint visibly without depending on a runtime validation pass.
 */
function compileField(field) {
    const isRequired = field.validation?.required === true;
    const baseLabel = humanizeFieldName(field.name);
    const label = isRequired ? `${baseLabel} *` : baseLabel;
    const lower = baseLabel.toLowerCase();
    const testid = `entity-field-${field.name}`;
    const id = fieldNodeId(field.name);
    // enum (explicit) → Select (the enum overrides the base type).
    const enumValues = field.validation?.enum;
    if (enumValues && enumValues.length > 0) {
        return {
            type: 'Select',
            id,
            props: {
                label,
                value: '',
                placeholder: `Select ${lower}`,
                options: enumValues.map((value) => ({ value, label: humanizeFieldName(value) })),
                testid,
            },
        };
    }
    // boolean → Checkbox. Structural `checked: false`; the container binds a boolean
    // `checked` + `onChange(boolean)` (NOT the string `value`/`onChange` path).
    if (field.type === 'boolean') {
        return {
            type: 'Checkbox',
            id,
            props: { label, checked: false, testid },
        };
    }
    // date | datetime → DatePicker. `value` is a YYYY-MM-DD string (the container
    // truncates a datetime's time-of-day when binding — v1 limitation).
    if (field.type === 'date' || field.type === 'datetime') {
        return {
            type: 'DatePicker',
            id,
            props: { label, value: '', placeholder: `Select ${lower}`, testid },
        };
    }
    // text → Textarea (multi-line). rows=4; maxLength carried through when declared.
    if (field.type === 'text') {
        const props = {
            label,
            value: '',
            placeholder: `Enter ${lower}`,
            rows: 4,
            testid,
        };
        if (typeof field.validation?.maxLength === 'number') {
            props.maxLength = field.validation.maxLength;
        }
        return { type: 'Textarea', id, props };
    }
    // string | number | json → Input.
    return {
        type: 'Input',
        id,
        props: {
            type: inputTypeForField(field.type),
            label,
            value: '',
            placeholder: `Enter ${lower}`,
            testid,
        },
    };
}
/**
 * Compile an EntityDefinition into a deterministic form WindowSpec.
 *
 * @param def - The entity definition (e.g. fetched from
 *   GET /api/entity/definitions/:slug).
 * @param options - Optional submit-label override.
 * @returns A serializable WindowSpec: a vertical Stack of field nodes followed by
 *   a submit Button.
 */
export function schemaToWindowSpec(def, options = {}) {
    const submitLabel = options.submitLabel ?? `Create ${def.label}`;
    const fieldNodes = def.fields.map(compileField);
    const submitButton = {
        type: 'Button',
        id: SUBMIT_BUTTON_ID,
        props: {
            label: submitLabel,
            variant: 'primary',
            testid: 'entity-form-submit',
        },
    };
    return {
        type: 'Stack',
        id: `entity-form-${def.slug}`,
        props: {
            gap: 'md',
            testid: `entity-form-${def.slug}`,
        },
        children: [...fieldNodes, submitButton],
    };
}
// ─── List / table view compiler (Faz-6 PR-4) ─────────────────────────────────
/**
 * A field is shown as a list/table COLUMN when it is promoted AND not long-form
 * text (a `text` field does not fit a column cell — it belongs in the detail form,
 * not the list). Column order = definition field order, so the first column is the
 * entity's primary field (name).
 *
 * EXPORTED (gh#1586): the compose-prompt entity data-tool catalog derives its per-tool
 * FIELD HINTS from the SAME rule, so the `{{field}}` interpolation names the LLM sees
 * are exactly the columns a compiled list would show — one source, no drift between the
 * deterministic list compiler and the LLM prompt catalog.
 */
export function isColumnField(field) {
    return field.promoted && field.type !== 'text';
}
/** Numeric / temporal columns right-align; everything else left-aligns. */
function alignForField(type) {
    return type === 'number' || type === 'date' || type === 'datetime' ? 'right' : 'left';
}
/**
 * Compile an EntityDefinition into a deterministic Table (list) WindowSpec.
 *
 * PURE — same definition → deeply-equal spec. The `dataSource.tool`
 * (`entity.<slug>.list`) is the runtime data feed (mapTo 'rows'); the structural
 * `rows: []` is empty and filled by the data layer at render time. Rendering the
 * fetched rows into the table is a downstream consumer's job (out of PR-4 scope) —
 * PR-4 owns the spec + its column derivation. `options.toolSlug` overrides the tool's
 * slug when it differs from `def.slug` (see EntityViewCompilerOptions).
 */
export function definitionToListSpec(def, options = {}) {
    const toolSlug = options.toolSlug ?? def.slug;
    const columns = def.fields.filter(isColumnField).map((field) => ({
        key: field.name,
        label: humanizeFieldName(field.name),
        align: alignForField(field.type),
    }));
    return {
        type: 'Table',
        id: `entity-list-${def.slug}`,
        props: {
            columns,
            rows: [],
            // Localized success-empty state (gh#1230): a successful fetch that returns no
            // rows shows TR "Kayıt yok" instead of the Table's English 'No data' default. The
            // rows-FETCH-FAILURE path (useDataSource) overwrites this with "Veri yüklenemedi",
            // so the two empty states stay Turkish AND distinguishable by text.
            emptyText: 'Kayıt yok',
            testid: `entity-list-${def.slug}`,
            // List-row drill-down descriptor (G4 gh#1571). PURE DATA — no closure in the JSON
            // spec (a fn can't serialize). The RENDERER (WindowSpecRenderer, flag-gated by
            // `composer.detail_drilldown_enabled`) reads this to bind an `onRowClick` closure that
            // opens `<toolSlug>.detail` with the clicked row's id. `opens` uses the SAME toolSlug as
            // the list feed so it targets THIS entity's registered detail view (`entityViewTemplates`
            // registers `.list`+`.detail`+`.form` together, so the detail view always exists).
            // `idField: 'id'` is the wire row's primary key (`EntityListRow.id: number`), the WHICH-
            // record parameter for `entity.<slug>.get`. When the flag is OFF the reader ignores this,
            // so rows stay non-interactive (pre-G4 behaviour) — a register-flip reader-ready ADD.
            rowClick: { opens: `${toolSlug}.detail`, idField: 'id' },
        },
        dataSource: {
            tool: `entity.${toolSlug}.list`,
            mapTo: 'rows',
        },
    };
}
// ─── Detail (single-record) view compiler (Gen-UI G1, roadmap #486) ──────────
/**
 * Compile an EntityDefinition into a deterministic read-only DETAIL WindowSpec — a
 * single-record view (roadmap #486, PR-B). Companion of the form/list compilers.
 *
 * PURE — same definition → deeply-equal spec. A vertical Stack of read-only Row nodes,
 * ONE per definition field, each carrying the stable `field-<name>` node id (so a
 * future detail container / cosmetic overlay binds by the SAME identity the form uses).
 * Each Row's `value` is a `{{<fieldName>}}` interpolation placeholder bound at render
 * time from the fetched record — the value is NEVER inlined into the stored spec (spec
 * carries structure, not data). Field names are the snake_case definition names; the
 * render container bridges them to the camelCase wire row via snakeToCamel (gh#1192,
 * identical to EntityList's read side).
 *
 * `dataSource.tool` is `entity.<slug>.get`; `args.id` is a `{{id}}` render-time
 * placeholder (WHICH record) — the detail container substitutes the concrete id from
 * its route/context. GATE-1 (specGate) accepts this: the tool matches the list|get
 * whitelist and `args.id` is a whitelisted scalar/placeholder. `options.toolSlug`
 * overrides the tool's slug when it differs from `def.slug` (see EntityViewCompilerOptions).
 */
export function definitionToDetailSpec(def, options = {}) {
    const toolSlug = options.toolSlug ?? def.slug;
    const fieldRows = def.fields.map((field) => ({
        type: 'Row',
        id: fieldNodeId(field.name),
        props: {
            // Static label = the humanized field name (the Row's PRIMARY line).
            primary: humanizeFieldName(field.name),
            // The field VALUE (the Row's SECONDARY line) — a `{{field}}` placeholder bound at render
            // time from the fetched record (snake→camel bridged in useDataSource.interpolate, gh#1192).
            // gh#1609 fix: the value was previously emitted into a top-level `value` prop that Row.tsx
            // does NOT render (it only renders primary/secondary/trailing) — so an opened detail showed
            // labels with BLANK values. `secondary` is a rendered slot (full-width under the label,
            // KULLANICI KARARI), so the bound value is now visible. GATE-1 unchanged: the `{{field}}`
            // mustache root is still the snake_case definition field (public-field scanned).
            secondary: `{{${field.name}}}`,
            testid: `entity-detail-field-${field.name}`,
        },
    }));
    return {
        type: 'Stack',
        id: `entity-detail-${def.slug}`,
        props: {
            gap: 'sm',
            testid: `entity-detail-${def.slug}`,
        },
        children: fieldRows,
        dataSource: {
            tool: `entity.${toolSlug}.get`,
            // Single-record DETAIL feed (G4 gh#1571): `mapTo: 'record'` tells the render layer
            // (useDataSource) to bind the ONE fetched record into the field Rows' `{{field}}`
            // placeholders and KEEP the children — as opposed to the default `'children'`, which
            // REPLACES children with an array. Emitting this was the missing wire (the default
            // silently produced a `'children'` mapping that dropped the detail Rows).
            mapTo: 'record',
            // Single-record fetch; `id` is a render-time placeholder (WHICH record), never
            // inlined into the stored spec. It is resolved by useDataSource against the render
            // context (openSpecWindow's `id`) BEFORE dispatch. GATE-1 accepts `{{id}}` as a
            // whitelisted scalar arg and does NOT scan it as a field reference.
            args: { id: '{{id}}' },
        },
    };
}
// ─── Board (kanban) view compiler (Faz-6 PR-4) ───────────────────────────────
/** The board always groups by the lifecycle status field. */
export const BOARD_GROUP_BY = 'status';
/**
 * Resolve the board's status DOMAIN (single source of truth): the definition's
 * explicit `statusValues`, else the `status` field's `validation.enum`. Throws when
 * neither exists — a board with no status domain is undefinable, so we fail loud
 * rather than emit an empty (schema-invalid) board.
 */
function resolveStatusValues(def) {
    if (def.statusValues && def.statusValues.length > 0)
        return def.statusValues;
    const statusField = def.fields.find((f) => f.name === BOARD_GROUP_BY);
    const enumValues = statusField?.validation?.enum;
    if (enumValues && enumValues.length > 0)
        return enumValues;
    throw new Error(`definitionToBoardSpec: entity "${def.slug}" has no status domain ` +
        '(requires statusValues or a status field with validation.enum)');
}
/**
 * Compile an EntityDefinition into a deterministic Board WindowSpec.
 *
 * PURE. Columns = status domain values mapped to `{ id, label }` (humanized).
 * `dataSource.tool` (`entity.<slug>.list`) is the row feed; the interactive board
 * (EntityBoard) reads columns + groupBy + dataSource.tool off this spec and owns
 * its own drag-drop container — the JSON spec stays handler-less. `options.toolSlug`
 * overrides the tool's slug when it differs from `def.slug` (see
 * EntityViewCompilerOptions — the designSystem case: def.slug='design_system' but the
 * live tool is `entity.designSystem.list`). Node ids / testids stay on `def.slug`.
 */
export function definitionToBoardSpec(def, options = {}) {
    const toolSlug = options.toolSlug ?? def.slug;
    const columns = resolveStatusValues(def).map((value) => ({
        id: value,
        label: humanizeFieldName(value),
    }));
    return {
        type: 'Board',
        id: `entity-board-${def.slug}`,
        props: {
            groupBy: BOARD_GROUP_BY,
            columns,
            testid: `entity-board-${def.slug}`,
        },
        dataSource: {
            tool: `entity.${toolSlug}.list`,
        },
    };
}
// ─── Sparse kanban rank helper (Faz-6 PR-4) ──────────────────────────────────
/**
 * Spacing between adjacent kanban positions. A large step leaves room to insert an
 * item strictly between two neighbours without renumbering the whole column
 * (fractional/sparse ranking).
 */
export const GAP_RANK_STEP = 1024;
/**
 * Compute a sparse kanban rank for an item dropped at `index` within a column whose
 * OTHER items already sit at `neighbours` (ascending positions, moved item
 * excluded). PURE + deterministic — no clock, random, or IO.
 *
 * Rules:
 *   - empty column            → GAP_RANK_STEP (1024)
 *   - top (index 0)           → neighbours[0] − STEP
 *   - bottom (index === len)  → neighbours[last] + STEP
 *   - middle                  → floor((prev + next) / 2)
 *   - gap < 1 (prev/next too close to fit a strictly-between integer, i.e.
 *     next − prev < 2) → renumber the whole column (1024, 2048, …) and return the
 *     moved item's slot plus the full renumbered list.
 */
export function computeGapRank(neighbours, index) {
    const len = neighbours.length;
    const clamped = Math.max(0, Math.min(index, len));
    if (len === 0)
        return { position: GAP_RANK_STEP };
    if (clamped === 0)
        return { position: neighbours[0] - GAP_RANK_STEP };
    if (clamped === len)
        return { position: neighbours[len - 1] + GAP_RANK_STEP };
    const prev = neighbours[clamped - 1];
    const next = neighbours[clamped];
    // Need at least one free integer strictly between prev and next.
    if (next - prev >= 2) {
        return { position: Math.floor((prev + next) / 2) };
    }
    // Too dense — renumber the whole column (moved item inserted at `clamped`).
    const rebalanced = Array.from({ length: len + 1 }, (_, i) => (i + 1) * GAP_RANK_STEP);
    return { position: rebalanced[clamped], rebalanced };
}
//# sourceMappingURL=entitySpecCompilers.js.map